import type {
  IClaimFormSectionConfig,
  IClaimFormFieldConfig,
  IClaimFormCustomField,
} from '@/@types/claims';

// LOCAL_TYPE_JUSTIFIED: editor-internal reducer action union — tightly scoped to this feature, not reused elsewhere
export type EditorAction =
  | { type: 'SET_SECTIONS'; sections: IClaimFormSectionConfig[] }
  | { type: 'ADD_SECTION'; section: IClaimFormSectionConfig }
  | { type: 'REMOVE_SECTION'; sectionKey: string }
  | { type: 'TOGGLE_SECTION_VISIBLE'; sectionKey: string }
  | { type: 'UPDATE_SECTION_LABEL'; sectionKey: string; label: string }
  | { type: 'MOVE_SECTION'; sectionKey: string; direction: 'up' | 'down' }
  | { type: 'REORDER_SECTIONS'; sectionKeys: string[] }
  | { type: 'ADD_FIELD'; sectionKey: string; field: IClaimFormFieldConfig }
  | { type: 'REMOVE_FIELD'; sectionKey: string; fieldKey: string }
  | { type: 'TOGGLE_FIELD_VISIBLE'; sectionKey: string; fieldKey: string }
  | { type: 'TOGGLE_FIELD_REQUIRED'; sectionKey: string; fieldKey: string }
  | { type: 'UPDATE_FIELD_LABEL'; sectionKey: string; fieldKey: string; label: string }
  | { type: 'UPDATE_FIELD_OPTIONS'; sectionKey: string; fieldKey: string; options: string[] }
  | { type: 'MOVE_FIELD'; sectionKey: string; fieldKey: string; direction: 'up' | 'down' }
  | { type: 'REORDER_FIELDS'; sectionKey: string; fieldKeys: string[] }
  | { type: 'SET_CUSTOM_FIELDS'; customFields: IClaimFormCustomField[] }
  | { type: 'ADD_CUSTOM_FIELD'; field: IClaimFormCustomField }
  | { type: 'REMOVE_CUSTOM_FIELD'; fieldId: string }
  | { type: 'UPDATE_CUSTOM_FIELD'; fieldId: string; updates: Partial<IClaimFormCustomField> }
  | { type: 'REORDER_CUSTOM_FIELDS'; fieldIds: string[] };

export interface IEditorState {
  sections: IClaimFormSectionConfig[];
  customFields: IClaimFormCustomField[];
}

function moveItem<T>(arr: T[], fromIdx: number, direction: 'up' | 'down'): T[] {
  const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1;
  if (toIdx < 0 || toIdx >= arr.length) return arr;
  const next = [...arr];
  [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
  return next.map((item, i) =>
    typeof item === 'object' && item !== null ? { ...item, sortOrder: i } : item
  );
}

export function editorReducer(state: IEditorState, action: EditorAction): IEditorState {
  switch (action.type) {
    case 'SET_SECTIONS':
      return { ...state, sections: action.sections };
    case 'ADD_SECTION':
      return { ...state, sections: [...state.sections, { ...action.section, sortOrder: state.sections.length }] };
    case 'REMOVE_SECTION':
      return {
        ...state,
        sections: state.sections
          .filter(s => s.sectionKey !== action.sectionKey)
          .map((s, i) => ({ ...s, sortOrder: i })),
      };
    case 'ADD_FIELD':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey !== action.sectionKey
            ? s
            : { ...s, fields: [...s.fields, { ...action.field, sortOrder: s.fields.length }] }
        ),
      };
    case 'REMOVE_FIELD':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey !== action.sectionKey
            ? s
            : { ...s, fields: s.fields.filter(f => f.fieldKey !== action.fieldKey).map((f, i) => ({ ...f, sortOrder: i })) }
        ),
      };
    case 'TOGGLE_SECTION_VISIBLE':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey === action.sectionKey ? { ...s, visible: !s.visible } : s
        ),
      };
    case 'UPDATE_SECTION_LABEL':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey === action.sectionKey ? { ...s, label: action.label } : s
        ),
      };
    case 'MOVE_SECTION': {
      const idx = state.sections.findIndex(s => s.sectionKey === action.sectionKey);
      return { ...state, sections: moveItem(state.sections, idx, action.direction) };
    }
    case 'TOGGLE_FIELD_VISIBLE':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey !== action.sectionKey
            ? s
            : {
                ...s,
                fields: s.fields.map(f =>
                  f.fieldKey === action.fieldKey ? { ...f, visible: !f.visible } : f
                ),
              }
        ),
      };
    case 'TOGGLE_FIELD_REQUIRED':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey !== action.sectionKey
            ? s
            : {
                ...s,
                fields: s.fields.map(f =>
                  f.fieldKey === action.fieldKey ? { ...f, required: !f.required } : f
                ),
              }
        ),
      };
    case 'UPDATE_FIELD_LABEL':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey !== action.sectionKey
            ? s
            : {
                ...s,
                fields: s.fields.map(f =>
                  f.fieldKey === action.fieldKey ? { ...f, label: action.label } : f
                ),
              }
        ),
      };
    case 'UPDATE_FIELD_OPTIONS':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey !== action.sectionKey
            ? s
            : {
                ...s,
                fields: s.fields.map(f =>
                  f.fieldKey === action.fieldKey ? { ...f, options: action.options } : f
                ),
              }
        ),
      };
    case 'MOVE_FIELD': {
      return {
        ...state,
        sections: state.sections.map(s => {
          if (s.sectionKey !== action.sectionKey) return s;
          const idx = s.fields.findIndex(f => f.fieldKey === action.fieldKey);
          return { ...s, fields: moveItem(s.fields, idx, action.direction) };
        }),
      };
    }
    case 'SET_CUSTOM_FIELDS':
      return { ...state, customFields: action.customFields };
    case 'ADD_CUSTOM_FIELD':
      return { ...state, customFields: [...state.customFields, action.field] };
    case 'REMOVE_CUSTOM_FIELD':
      return {
        ...state,
        customFields: state.customFields.filter(f => f.id !== action.fieldId),
      };
    case 'UPDATE_CUSTOM_FIELD':
      return {
        ...state,
        customFields: state.customFields.map(f =>
          f.id === action.fieldId ? { ...f, ...action.updates } : f
        ),
      };
    case 'REORDER_SECTIONS': {
      const reordered = action.sectionKeys
        .map(key => state.sections.find(s => s.sectionKey === key))
        .filter((s): s is IClaimFormSectionConfig => s !== undefined)
        .map((s, i) => ({ ...s, sortOrder: i }));
      return { ...state, sections: reordered };
    }
    case 'REORDER_FIELDS': {
      return {
        ...state,
        sections: state.sections.map(s => {
          if (s.sectionKey !== action.sectionKey) return s;
          const reordered = action.fieldKeys
            .map(key => s.fields.find(f => f.fieldKey === key))
            .filter((f): f is IClaimFormFieldConfig => f !== undefined)
            .map((f, i) => ({ ...f, sortOrder: i }));
          return { ...s, fields: reordered };
        }),
      };
    }
    case 'REORDER_CUSTOM_FIELDS': {
      const reordered = action.fieldIds
        .map(id => state.customFields.find(f => f.id === id))
        .filter((f): f is IClaimFormCustomField => f !== undefined)
        .map((f, i) => ({ ...f, sortOrder: i }));
      return { ...state, customFields: reordered };
    }
    default:
      return state;
  }
}
